import React, { useEffect, useRef, useState } from 'react';
import { getRouteApi, useNavigate, Link } from '@tanstack/react-router';
import { applyActionCode } from 'firebase/auth';
import { DiscovrLogo } from '@discovr/ui/design-system';
import { auth } from '@discovr/common';
import { Loader2, XCircle, ShieldCheck } from '@discovr/ui/icons';
import { Button } from '@discovr/ui';
import { runtimeConfig } from '@discovr/common/runtime-config';

const LANDING_URL = runtimeConfig('LANDING_URL') ?? 'https://getdiscovr.ai';

type ActionState = 'loading' | 'error';

/**
 * Handles Firebase email action links (/auth/action?mode=verifyEmail&oobCode=...).
 *
 * The Firebase Console action URL should point to:
 *   https://your-domain.com/auth/action
 *
 * On success: redirects to the shared landing waitlist-confirmed page.
 * On error: shows an in-app error with a retry option.
 */
const authActionRoute = getRouteApi('/auth/action');

export const EmailActionHandler: React.FC = () => {
  const navigate = useNavigate();
  const { mode, oobCode } = authActionRoute.useSearch();

  const isInvalidLink = !oobCode || (mode !== 'verifyEmail' && mode !== 'resetPassword');
  const [state, setState] = useState<ActionState>(() => (isInvalidLink ? 'error' : 'loading'));
  const [errorMessage, setErrorMessage] = useState(() =>
    isInvalidLink ? 'Invalid or missing verification link. Please request a new one.' : '',
  );
  const ranEffect = useRef(false);

  useEffect(() => {
    if (ranEffect.current) return;
    ranEffect.current = true;

    // resetPassword — delegate to its own page
    if (mode === 'resetPassword') {
      navigate({ to: '/reset-password', search: { oobCode }, replace: true });
      return;
    }

    // Missing or unknown action code — already handled by initial state
    if (isInvalidLink) return;

    applyActionCode(auth, oobCode)
      .then(async () => {
        // Reload so Firebase reflects emailVerified=true immediately.
        try {
          await auth.currentUser?.reload();
        } catch {
          /* ignore */
        }

        // Email verification is auto-synced from Firebase claims on next login.

        // Redirect to the shared waitlist-confirmed page on the landing site.
        window.location.href = `${LANDING_URL}/email-verified?type=brand`;
      })
      .catch((err) => {
        const code: string = err?.code ?? '';
        let msg = 'Verification failed. The link may be invalid or expired.';
        if (code === 'auth/invalid-action-code') {
          msg =
            'This verification link has already been used or has expired. Please request a new one.';
        } else if (code === 'auth/expired-action-code') {
          msg = 'This verification link has expired. Please request a new one.';
        } else if (code === 'auth/user-disabled') {
          msg = 'Your account has been disabled. Please contact support.';
        }
        setErrorMessage(msg);
        setState('error');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="auth-split-layout">
      <Link to="/" className="auth-back-btn">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </Link>

      {/* Left branding panel */}
      <div className="auth-split-left">
        <div className="auth-split-left-content animate-fade-in">
          <Link to="/" className="auth-split-logo">
            <DiscovrLogo markOnly size={120} />
            Discovr
          </Link>
          <div className="auth-split-main-content">
            <h1>Almost there.</h1>
            <p>
              Verifying your email confirms your identity and secures your spot on the Discovr
              platform.
            </p>
            <div className="auth-split-features">
              <div className="creator-feature">
                <div className="creator-feature-icon">✓</div>
                <span>Secure, one-click verification</span>
              </div>
              <div className="creator-feature">
                <div className="creator-feature-icon">✓</div>
                <span>Instant access after confirmation</span>
              </div>
              <div className="creator-feature">
                <div className="creator-feature-icon">✓</div>
                <span>Your data is encrypted and safe</span>
              </div>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
            © {new Date().getFullYear()} Discovr. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right action panel */}
      <div className="auth-split-right">
        <div className="auth-form-wrapper">
          <div className="auth-form-container animate-fade-in">
            {/* ── Loading / redirecting ── */}
            {state === 'loading' && (
              <div className="auth-success-state" style={{ padding: 'var(--space-6) 0' }}>
                <div className="auth-success-icon" style={{ marginBottom: 'var(--space-4)' }}>
                  <Loader2
                    className="animate-spin"
                    size={52}
                    style={{ color: 'var(--color-primary)', display: 'block', margin: '0 auto' }}
                  />
                </div>
                <h2 style={{ marginBottom: 'var(--space-2)' }}>Verifying your email…</h2>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                  Hang tight, this only takes a second.
                </p>
              </div>
            )}

            {/* ── Error ── */}
            {state === 'error' && (
              <div
                className="auth-success-state animate-fade-in"
                style={{ padding: 'var(--space-6) 0' }}
              >
                <div className="auth-success-icon" style={{ marginBottom: 'var(--space-4)' }}>
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background:
                        'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))',
                      border: '2px solid rgba(239,68,68,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                  >
                    <XCircle size={40} color="#ef4444" />
                  </div>
                </div>
                <h2 style={{ marginBottom: 'var(--space-2)' }}>Verification Failed</h2>
                <p
                  style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '14px',
                    marginBottom: 'var(--space-6)',
                    lineHeight: 1.6,
                  }}
                >
                  {errorMessage}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <Button
                    onClick={() => navigate({ to: '/login' })}
                    size="lg"
                    style={{ width: '100%' }}
                  >
                    Request a new link
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate({ to: '/login' })}
                    style={{ width: '100%' }}
                  >
                    Back to Login
                  </Button>
                </div>
              </div>
            )}

            {/* Security note */}
            <div
              style={{
                marginTop: 'var(--space-6)',
                padding: '12px 16px',
                background: 'rgba(124,58,237,0.06)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid rgba(124,58,237,0.12)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <ShieldCheck
                size={16}
                style={{ color: 'var(--color-primary)', marginTop: 2, flexShrink: 0 }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                  lineHeight: 1.6,
                }}
              >
                This link is single-use and expires after 24 hours for your security.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailActionHandler;
